/**
 * ProposalFormationDrawer
 *
 * Five-step workflow for packaging a completed session result into a
 * catchball_proposals row and submitting it upward (Handoff).
 *
 * Brief B — Proposal Formation Surface
 */

import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Send,
  ClipboardList,
  MessageSquare,
  MapPin,
  Package,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { ProposalAnnotationDraftAssist } from './ExchangeDraftAssist';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import {
  type PlanningMaturity,
  PLANNING_MATURITY_LABELS,
} from '../../../types/prioritization/itemLifecycle';
import { CATCHBALL_LABELS } from '@/lib/catchball/vocabulary';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface TradeoffEntry {
  what: string;
  why: string;
}

interface DisagreementEntry {
  item: string;
  reason: string;
}

export interface ProposalFormationDrawerProps {
  isOpen: boolean;
  onClose: () => void;

  sessionId: string;
  /** Display name of the originating session (§3 provenance) */
  sessionName?: string;
  /** ISO date string of the session's last update / run date */
  sessionDate?: string;
  /** Name of the session facilitator */
  facilitatorName?: string;
  /** Result status from session_results (complete | published) */
  resultStatus?: string;

  loopId: string;
  loopNumber?: number;
  cycleId: string;
  cycleName?: string;
  trackId?: string;
  trackName?: string;

  rankedItemIds: string[];
  scores?: Record<string, Record<string, number>>;

  actorId: string;
  actorName: string;
  workspaceId?: string;

  /** Called with the proposal ID after successful submission */
  onProposalSubmitted?: (proposalId: string, submittedAt: string) => void;
}

// ─── Step indicator ────────────────────────────────────────────────────────────

const STEPS = [
  { id: 'review',   label: 'Review',  icon: ClipboardList },
  { id: 'annotate', label: 'Annotate', icon: MessageSquare },
  { id: 'scope',    label: 'Scope',    icon: MapPin },
  { id: 'package',  label: 'Package',  icon: Package },
  { id: 'submit',   label: 'Submit',   icon: Send },
] as const;

type Step = typeof STEPS[number]['id'];

// ─── Component ─────────────────────────────────────────────────────────────────

export function ProposalFormationDrawer({
  isOpen,
  onClose,
  sessionId,
  sessionName,
  sessionDate,
  facilitatorName,
  resultStatus,
  loopId,
  loopNumber,
  cycleId,
  cycleName,
  trackId,
  trackName,
  rankedItemIds,
  scores = {},
  actorId,
  actorName,
  workspaceId = '',
  onProposalSubmitted,
}: ProposalFormationDrawerProps) {
  const items = useItemsStore(s => s.items);
  const formProposal = useCatchballStore(s => s.formProposal);
  const submitProposal = useCatchballStore(s => s.submitProposal);
  // Read-only: the loop when this session's store already has it (no fetch) —
  // enriches the drafting-aid context packet; absent = named omissions.
  const draftAssistLoop = useCatchballLoopStore(
    s => s.loops.find(l => l.id === loopId) ?? null,
  );

  // Drawer UI state
  const [activeStep, setActiveStep] = useState<Step>('review');

  // Formation state
  const [isForming, setIsForming] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [proposalId, setProposalId] = useState<string | null>(null);

  // Form fields (Step 2 — Annotate)
  const [rationale, setRationale] = useState('');
  const [tradeoffText, setTradeoffText] = useState('');
  const [disagreementsText, setDisagreementsText] = useState('');
  const [assumptions, setAssumptions] = useState('');
  const [showTradeoffs, setShowTradeoffs] = useState(false);
  const [showDisagreements, setShowDisagreements] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  /** Advisory scope note from the drafting aid — display only, never submitted. */
  const [aiScopeDraft, setAiScopeDraft] = useState<string | null>(null);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);

  // ─── On open: call formProposal() to create/resume draft ─────────────────────

  const initFormation = useCallback(async () => {
    if (!trackId) {
      setFormError('This session is not linked to a track. A track is required to form a proposal.');
      return;
    }
    setIsForming(true);
    setFormError(null);
    try {
      const result = await formProposal({
        sessionId,
        trackId,
        loopId,
        cycleId,
        workspaceId,
        actorId,
        actorName,
      });
      if (!result) {
        setFormError(
          useCatchballStore.getState().error ??
            'Could not initialize proposal. Check that the session has results and the loop is active.',
        );
        return;
      }
      setProposalId(result.proposalId);

      // Resume scenario: load any previously-saved rationale
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { data: draft } = await supabase
          .from('catchball_proposals')
          .select('rationale, tradeoffs, status')
          .eq('id', result.proposalId)
          .maybeSingle();
        if (draft) {
          if (draft.status === 'proposed' || draft.status === 'pending') {
            // Already submitted — show submitted state
            setIsSubmitted(true);
          } else {
            if (draft.rationale) setRationale(draft.rationale);
            if (draft.tradeoffs && Array.isArray(draft.tradeoffs) && draft.tradeoffs.length > 0) {
              setTradeoffText(
                (draft.tradeoffs as TradeoffEntry[]).map((t) => `${t.what}: ${t.why}`).join('\n')
              );
              setShowTradeoffs(true);
            }
          }
        }
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to initialize proposal formation.');
    } finally {
      setIsForming(false);
    }
  }, [sessionId, trackId, loopId, cycleId, workspaceId, actorId, actorName, formProposal]);

  useEffect(() => {
    if (isOpen && !proposalId && !isForming && !isSubmitted) {
      void initFormation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─── Submit handler ──────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!proposalId) return;
    setIsSubmitting(true);
    setSubmitError(null);

    // Build structured tradeoffs from freetext (simple line-by-line)
    const tradeoffs: TradeoffEntry[] = tradeoffText.trim()
      ? tradeoffText.split('\n').filter(Boolean).map((line) => ({ what: line, why: '' }))
      : [];
    const disagreements: DisagreementEntry[] = disagreementsText.trim()
      ? disagreementsText.split('\n').filter(Boolean).map((line) => ({ item: line, reason: '' }))
      : [];

    try {
      const success = await submitProposal({
        proposalId,
        rationale,
        tradeoffs: tradeoffs.length ? tradeoffs : undefined,
        disagreements: disagreements.length ? disagreements : undefined,
        assumptions: assumptions.trim() || undefined,
        actorId,
        workspaceId,
      });

      if (!success) {
        // The store records the server's actual refusal (RPC error or the
        // RPC's own { success:false, error }) — show that, not a guess.
        const serverReason = useCatchballStore.getState().error;
        setSubmitError(
          serverReason
            ? `Submission refused: ${serverReason}`
            : 'Submission failed. Try again.',
        );
        return;
      }

      const now = new Date().toISOString();
      setIsSubmitted(true);
      setSubmittedAt(now);
      setShowConfirm(false);
      onProposalSubmitted?.(proposalId, now);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Reset on close ──────────────────────────────────────────────────────────

  const handleClose = () => {
    onClose();
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const rationaleIsValid = rationale.trim().length > 0;
  const canAdvanceToSubmit = rationaleIsValid;

  const stepIndex = STEPS.findIndex((s) => s.id === activeStep);

  const totalScore = (itemId: string) => {
    const s = scores[itemId] ?? {};
    return s.total ?? Object.entries(s).reduce((sum, [k, v]) => (k === 'total' ? sum : sum + v), 0);
  };

  // Deterministic provenance summary for the drafting aid — the same facts the
  // Review step shows, serialized. The AI may cite only what is in here.
  const sessionResultSummary = [
    sessionName ? `Session: ${sessionName}` : null,
    facilitatorName || sessionDate
      ? `Run by ${facilitatorName ?? 'unknown facilitator'}${sessionDate ? ` on ${new Date(sessionDate).toLocaleDateString()}` : ''}`
      : null,
    resultStatus ? `Result status: ${resultStatus}` : null,
    trackName ? `Track: ${trackName}` : null,
    cycleName ? `Cycle: ${cycleName}` : null,
    'Ranked result:',
    ...rankedItemIds.map((itemId, index) => {
      const item = items.find((c) => c.id === itemId);
      const score = totalScore(itemId);
      return `#${index + 1} ${item?.title ?? itemId}${score > 0 ? ` (score ${score})` : ''}`;
    }),
  ]
    .filter(Boolean)
    .join('\n');

  if (!isOpen) return null;

  // ─── Portal render ───────────────────────────────────────────────────────────

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer panel — right side */}
      <div className="relative ml-auto flex h-full w-full max-w-xl flex-col bg-[var(--bg-raised)] shadow-2xl">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 border-b border-[var(--rule)] px-6 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">Proposal Formation</p>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">Form Proposal</h2>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">Ranking compares. Priority commits.</p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Step tabs ──────────────────────────────────────────────────────── */}
        <div className="flex border-b border-[var(--rule)] bg-[var(--surface)] px-4">
          {STEPS.map((step, idx) => {
            const isActive = step.id === activeStep;
            const isDone = idx < stepIndex;
            const Icon = step.icon;
            return (
              <button
                key={step.id}
                onClick={() => {
                  if (step.id === 'submit' && !canAdvanceToSubmit) return;
                  setActiveStep(step.id);
                }}
                disabled={step.id === 'submit' && !canAdvanceToSubmit && !isSubmitted}
                className={`flex flex-1 flex-col items-center gap-0.5 py-3 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isActive
                    ? 'border-b-2 border-primary-600 text-primary-700'
                    : isDone
                      ? 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                      : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <span className="relative">
                  <Icon className="h-3.5 w-3.5" />
                  {isDone && (
                    <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary-500" />
                  )}
                </span>
                {step.label}
              </button>
            );
          })}
        </div>

        {/* ── Body ───────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Initializing overlay */}
          {isForming && (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-[var(--text-ter)]">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
              <p className="text-sm">Initializing proposal draft…</p>
            </div>
          )}

          {/* Init error */}
          {!isForming && formError && (
            <div className="m-6 border border-[var(--coral)] bg-[var(--coral-dim)] p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--coral)]">Could not initialize</p>
                <p className="text-sm text-[var(--coral)] mt-0.5">{formError}</p>
              </div>
            </div>
          )}

          {/* Already submitted state */}
          {!isForming && !formError && isSubmitted && (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--green-dim)]">
                <CheckCircle2 className="h-7 w-7 text-[var(--green)]" />
              </div>
              <div>
                <p className="text-base font-semibold text-[var(--text-pri)]">Proposal Submitted</p>
                {submittedAt && (
                  <p className="text-sm text-[var(--text-ter)] mt-1">
                    Submitted {new Date(submittedAt).toLocaleString()}
                  </p>
                )}
                <p className="text-sm text-[var(--text-ter)] mt-2">
                  This proposal is now with the executive for review. You cannot edit it without a return.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="mt-2 inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {/* Main form content */}
          {!isForming && !formError && !isSubmitted && proposalId && (
            <div className="px-6 py-5 space-y-6">

              {/* ── STEP 1: Review ─────────────────────────────────────────── */}
              {activeStep === 'review' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)]">Session Result</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      This is the result you are proposing. It cannot be changed here.
                    </p>
                  </div>

                  {/* Provenance block (§3 — UI provenance on formation surface) */}
                  <div className="border border-[var(--rule)] bg-[var(--surface)] divide-y divide-[var(--rule)] overflow-hidden text-xs">
                    {sessionName && (
                      <div className="flex items-start justify-between px-3 py-2 gap-2">
                        <span className="text-[var(--text-ter)] shrink-0">Based on session</span>
                        <span className="text-[var(--text-pri)] font-medium text-right">{sessionName}</span>
                      </div>
                    )}
                    {(facilitatorName || sessionDate) && (
                      <div className="flex items-start justify-between px-3 py-2 gap-2">
                        <span className="text-[var(--text-ter)] shrink-0">Run by</span>
                        <span className="text-[var(--text-pri)] font-medium text-right">
                          {facilitatorName ?? '—'}
                          {sessionDate && (
                            <span className="text-[var(--text-ter)] font-normal ml-1">
                              on {new Date(sessionDate).toLocaleDateString()}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {resultStatus && (
                      <div className="flex items-start justify-between px-3 py-2 gap-2">
                        <span className="text-[var(--text-ter)] shrink-0">Result status</span>
                        <span className={`font-medium capitalize ${resultStatus === 'published' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                          {resultStatus}
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between px-3 py-2 gap-2">
                      <span className="text-[var(--text-ter)] shrink-0">Loop · Cycle</span>
                      <span className="text-[var(--text-pri)] font-medium text-right">
                        {loopNumber != null ? `Loop #${loopNumber}` : 'Loop'}
                        {cycleName ? ` · ${cycleName}` : ''}
                      </span>
                    </div>
                  </div>

                  <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
                    <table className="w-full">
                      <thead className="bg-[var(--surface)] border-b border-[var(--rule)]">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-medium text-[var(--text-ter)] w-10">Rank</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-[var(--text-ter)]">Item</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-[var(--text-ter)] w-28">Maturity</th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-[var(--text-ter)] w-16">Score</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--rule)]">
                        {rankedItemIds.map((itemId, index) => {
                          const item = items.find((c) => c.id === itemId);
                          const score = totalScore(itemId);
                          const maturity: PlanningMaturity = item?.planning_maturity ?? 'directional';
                          const isDirectional = maturity === 'directional';
                          return (
                            <tr key={itemId} className="hover:bg-[var(--surface)]">
                              <td className="px-4 py-2.5 text-xs font-bold text-[var(--text-ter)]">#{index + 1}</td>
                              <td className="px-4 py-2.5 text-sm text-[var(--text-pri)]">
                                {item?.title ?? itemId}
                                <ItemRelationshipFlags itemId={itemId} className="ml-2" />
                              </td>
                              <td className="px-4 py-2.5">
                                <span
                                  title={isDirectional ? 'Directional: planning serves prioritization, not execution' : undefined}
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                    isDirectional
                                      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]'
                                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)] border border-[var(--rule)]'
                                  }`}
                                >
                                  {PLANNING_MATURITY_LABELS[maturity]}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right text-xs text-[var(--text-ter)]">
                                {score > 0 ? score : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => setActiveStep('annotate')}
                      className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                    >
                      Next: Annotate →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: Annotate ───────────────────────────────────────── */}
              {activeStep === 'annotate' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)]">Annotate</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Add context that explains and supports this ranking.
                    </p>
                  </div>

                  {/* Slice 3 — Exchange drafting aid ("draft, never throw").
                      Renders nothing when the exchange_drafting gate is off;
                      fills ONLY the local fields below — submission stays on
                      the existing Submit flow. */}
                  <ProposalAnnotationDraftAssist
                    loop={draftAssistLoop}
                    loopId={loopId}
                    cycleId={cycleId}
                    loopNumber={loopNumber}
                    sessionResultSummary={sessionResultSummary}
                    onApply={(drafts) => {
                      if (drafts.rationale) setRationale(drafts.rationale);
                      if (drafts.tradeoffs) {
                        setTradeoffText(drafts.tradeoffs);
                        setShowTradeoffs(true);
                      }
                      if (drafts.assumptions) {
                        setAssumptions(drafts.assumptions);
                        setShowAssumptions(true);
                      }
                      if (drafts.scope) setAiScopeDraft(drafts.scope);
                    }}
                  />

                  {/* Rationale — required */}
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-pri)] mb-1">
                      Rationale
                      <span className="ml-1.5 text-xs font-normal text-[var(--coral)]">Required</span>
                    </label>
                    <p className="text-xs text-[var(--text-ter)] mb-2">
                      Why does this ranking reflect the right priorities? Reference Directional Planning signals (effort, risk, dependencies, capacity, confidence) that shaped the comparison.
                    </p>
                    <textarea
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                      placeholder="Describe the reasoning behind this ranking. Note key Directional Planning signals — rough effort, known constraints, confidence levels, dependencies — that informed the comparison…"
                      rows={5}
                      className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y"
                    />
                    {!rationaleIsValid && (
                      <p className="mt-1 text-xs text-[var(--text-ter)]">Cannot submit without rationale</p>
                    )}
                  </div>

                  {/* Tradeoffs — optional, collapsible */}
                  <div className="border border-[var(--rule)] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowTradeoffs((v) => !v)}
                      className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      Tradeoffs <span className="text-xs text-[var(--text-ter)] font-normal">Optional</span>
                      {showTradeoffs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showTradeoffs && (
                      <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3">
                        <p className="text-xs text-[var(--text-ter)] mb-2">
                          What did you choose not to prioritize, and why?
                        </p>
                        <textarea
                          value={tradeoffText}
                          onChange={(e) => setTradeoffText(e.target.value)}
                          placeholder="One tradeoff per line…"
                          rows={3}
                          className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y"
                        />
                      </div>
                    )}
                  </div>

                  {/* Disagreements — optional, collapsible */}
                  <div className="border border-[var(--rule)] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowDisagreements((v) => !v)}
                      className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      Disagreements <span className="text-xs text-[var(--text-ter)] font-normal">Optional</span>
                      {showDisagreements ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showDisagreements && (
                      <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3">
                        <p className="text-xs text-[var(--text-ter)] mb-2">
                          Are there items you disagree with in this ranking? Note them here.
                        </p>
                        <textarea
                          value={disagreementsText}
                          onChange={(e) => setDisagreementsText(e.target.value)}
                          placeholder="One disagreement per line…"
                          rows={3}
                          className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y"
                        />
                      </div>
                    )}
                  </div>

                  {/* Assumptions — optional, collapsible */}
                  <div className="border border-[var(--rule)] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowAssumptions((v) => !v)}
                      className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      Assumptions <span className="text-xs text-[var(--text-ter)] font-normal">Optional</span>
                      {showAssumptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {showAssumptions && (
                      <div className="border-t border-[var(--rule)] px-4 pb-4 pt-3">
                        <p className="text-xs text-[var(--text-ter)] mb-2">
                          What had to be true for this ranking to make sense?
                        </p>
                        <textarea
                          value={assumptions}
                          onChange={(e) => setAssumptions(e.target.value)}
                          placeholder="Describe key assumptions…"
                          rows={3}
                          className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setActiveStep('review')}
                      className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={() => setActiveStep('scope')}
                      className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                    >
                      Next: Scope →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 3: Scope ──────────────────────────────────────────── */}
              {activeStep === 'scope' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)]">Scope Confirmation</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Confirm the proposal belongs to the correct context.
                    </p>
                  </div>

                  <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] overflow-hidden">
                    <ScopeRow label="Track" value={trackName ?? '—'} />
                    <ScopeRow
                      label="Loop"
                      value={loopNumber != null ? `Loop #${loopNumber}` : loopId.slice(0, 8) + '…'}
                    />
                    <ScopeRow label="Cycle" value={cycleName ?? '—'} />
                    <ScopeRow label="Direction" value="Upward (Handoff to executive)" />
                  </div>

                  {aiScopeDraft && (
                    <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] mb-1">
                        AI scope note (advisory — not submitted)
                      </p>
                      <p className="text-sm text-[var(--text-sec)] leading-relaxed whitespace-pre-wrap">{aiScopeDraft}</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setActiveStep('annotate')}
                      className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={() => setActiveStep('package')}
                      className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                    >
                      Next: Package →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 4: Package ────────────────────────────────────────── */}
              {activeStep === 'package' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)]">Package</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Review what will be attached to this proposal before submitting.
                    </p>
                  </div>

                  {/* Ready indicator */}
                  <div className={` border p-4 ${
                    canAdvanceToSubmit
                      ? 'border-[var(--green)] bg-[var(--green-dim)]'
                      : 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
                  }`}>
                    <div className="flex items-start gap-3">
                      {canAdvanceToSubmit ? (
                        <CheckCircle2 className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${canAdvanceToSubmit ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`}>
                          {canAdvanceToSubmit ? 'Ready to submit' : 'Rationale required before submitting'}
                        </p>
                        {!canAdvanceToSubmit && (
                          <button
                            onClick={() => setActiveStep('annotate')}
                            className="mt-1 text-xs text-[var(--mustard)] underline hover:no-underline"
                          >
                            Go back to Annotate to add rationale
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Attachment summary */}
                  <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] overflow-hidden">
                    <ScopeRow label="Ranked items" value={`${rankedItemIds.length} item${rankedItemIds.length !== 1 ? 's' : ''}`} />
                    <ScopeRow label="Session source" value={sessionId.slice(0, 8) + '…'} />
                    <ScopeRow label="Planning basis" value="Directional Planning" valueClass="text-[var(--mustard)]" />
                    <ScopeRow label="Rationale" value={rationaleIsValid ? '✓ Provided' : '— Missing'} valueClass={rationaleIsValid ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'} />
                    <ScopeRow label="Tradeoffs" value={tradeoffText.trim() ? '✓ Provided' : '— Not provided'} />
                    <ScopeRow label="Assumptions" value={assumptions.trim() ? '✓ Provided' : '— Not provided'} />
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setActiveStep('scope')}
                      className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={() => setActiveStep('submit')}
                      disabled={!canAdvanceToSubmit}
                      className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next: Submit →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 5: Submit ─────────────────────────────────────────── */}
              {activeStep === 'submit' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)]">{CATCHBALL_LABELS.submitProposal}</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Review your proposal one last time before sending it upward.
                    </p>
                  </div>

                  {/* Commitment framing */}
                  <div className="flex items-start gap-3 border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3">
                    <AlertCircle className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-[var(--text-sec)] leading-relaxed">
                      <span className="font-semibold">You are packaging a ranking output into a Priority Proposal.</span>
                      {' '}Acceptance by the governing tier commits this as an organizational priority.
                      The ranking becomes an Accepted Priority — not just a preference signal — only after it clears Catchball.
                    </p>
                  </div>

                  {/* Summary card */}
                  <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Rationale</p>
                      <p className="text-sm text-[var(--text-sec)] leading-relaxed whitespace-pre-wrap">{rationale}</p>
                    </div>
                    <div className="h-px bg-[var(--hover-tint)]" />
                    <div className="flex items-center gap-6 text-xs text-[var(--text-ter)]">
                      <span>{rankedItemIds.length} items</span>
                      <span>{trackName ?? 'No Track'}</span>
                      <span>{cycleName ?? 'No cycle'}</span>
                    </div>
                  </div>

                  {submitError && (
                    <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[var(--coral)]">{submitError}</p>
                    </div>
                  )}

                  {/* Confirmation dialog — inline */}
                  {showConfirm ? (
                    <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4 space-y-3">
                      <p className="text-sm font-medium text-[var(--mustard)]">
                        Once submitted, this proposal moves to the executive for review. You cannot edit it without a return.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                        >
                          {isSubmitting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                          {isSubmitting ? 'Submitting…' : 'Confirm Submit'}
                        </button>
                        <button
                          onClick={() => setShowConfirm(false)}
                          disabled={isSubmitting}
                          className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <button
                        onClick={() => setActiveStep('package')}
                        className="inline-flex items-center gap-2 border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        onClick={() => setShowConfirm(true)}
                        disabled={!canAdvanceToSubmit}
                        className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="h-3.5 w-3.5" />
                        {CATCHBALL_LABELS.submitProposal}
                      </button>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Helper: scope row ─────────────────────────────────────────────────────────

function ScopeRow({
  label,
  value,
  valueClass = 'text-[var(--text-sec)]',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs font-medium text-[var(--text-ter)]">{label}</span>
      <span className={`text-sm font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}
